import axios from "../axios";

/**
 * Store collections API — CRUD for Store::Collection.
 *
 * Routes reference:
 * - GET    /api/store/collections         — list all. Response: { collections: [...] } or { data: [...] }
 * - POST   /api/store/collections         — body: { "collection": { "name": "Collection Name" } }. Returns 201 + created collection or 422 { "error": "..." }
 * - PATCH  /api/store/collections/:id     — body: { "collection": { "name": "New Name" } }. Returns updated collection or 422
 * - DELETE /api/store/collections/:id    — no body. Returns 204 on success; 422 or 404 { "error": "Collection not found" }
 */

const COLLECTIONS_BASE = "/store/collections";

/**
 * GET /api/store/collections — list all collections.
 * @returns {Promise<Array<{ id: number|string, name: string }>>}
 */
export const getCollections = async () => {
  const body = await axios.get(COLLECTIONS_BASE);
  if (Array.isArray(body)) return body;
  if (body?.collections && Array.isArray(body.collections))
    return body.collections;
  if (body?.data && Array.isArray(body.data)) return body.data;
  return [];
};

/**
 * GET /api/store/collections/:identifier — single collection with products.
 * @param {string} identifier
 * @returns {Promise<{ collection: object | null, products: Array }>}
 */
export const getCollectionWithProducts = async (identifier) => {
  const body = await axios.get(`${COLLECTIONS_BASE}/${identifier}`);

  const collection =
    body?.collection ??
    body?.data?.collection ??
    (body && body.name ? body : null);

  let products =
    body?.products ??
    body?.data?.products ??
    (Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : []);

  if (!Array.isArray(products)) products = [];

  return { collection, products };
};

/**
 * POST /api/store/collections — create a collection.
 * @param {string} name
 * @param {string|null|undefined} identifier
 * @returns {Promise<{ id: number|string, name: string }>}
 */
export const createCollection = async (name, identifier) => {
  const payload = {
    name: String(name).trim(),
  };

  const trimmedIdentifier = identifier != null ? String(identifier).trim() : "";

  if (trimmedIdentifier) {
    payload.identifier = trimmedIdentifier;
  }

  return axios.post(COLLECTIONS_BASE, {
    collection: payload,
  });
};

/**
 * PATCH /api/store/collections/:id — update a collection.
 * @param {number|string} id
 * @param {string} name
 * @param {string|null|undefined} identifier
 * @returns {Promise<{ id: number|string, name: string }>}
 */
export const updateCollection = async (id, name, identifier) => {
  const payload = {
    name: String(name).trim(),
  };

  const trimmedIdentifier = identifier != null ? String(identifier).trim() : "";

  if (trimmedIdentifier) {
    payload.identifier = trimmedIdentifier;
  }

  return axios.patch(`${COLLECTIONS_BASE}/${id}`, {
    collection: payload,
  });
};

/**
 * DELETE /api/store/collections/:id — delete a collection.
 * On success returns nothing (204). On 404/422 throws with err.error.
 */
export const deleteCollection = async (id) => {
  await axios.delete(`${COLLECTIONS_BASE}/${id}`);
};

/**
 * Store collection products API — link a product to a collection.
 *
 * Routes reference:
 * - POST   /api/store/collection_products     — body: { "collection_product": { "store_collection_id": 1, "product_id": 123 } }. Returns the created join record.
 * - DELETE /api/store/collection_products/:id  — no body. Returns 204 on success; 404/422 with { "error": "..." }.
 */
const COLLECTION_PRODUCTS_BASE = "/store/collection_products";

/**
 * POST /api/store/collection_products — add a product to a collection.
 * @param {number|string} storeCollectionId
 * @param {number|string} productId
 * @returns {Promise<object>} Created join record
 */
export const createCollectionProduct = async (storeCollectionId, productId) => {
  return axios.post(COLLECTION_PRODUCTS_BASE, {
    collection_product: {
      store_collection_id: Number(storeCollectionId),
      product_id: Number(productId),
    },
  });
};

/**
 * DELETE /api/store/collection_products/:id — remove a product from a collection.
 * @param {number|string} collectionProductId — id of the collection_product join record
 */
export const deleteCollectionProduct = async (collectionProductId) => {
  await axios.delete(`${COLLECTION_PRODUCTS_BASE}/${collectionProductId}`);
};
